/**
 * Matter Quick Access Store
 *
 * Tracks pinned + recently-accessed matters for the "Jump back in" widget.
 * Persisted to localStorage for continuity across reloads.
 *
 * CONFIDENTIALITY (critical): these entries hold client matter NAMES and
 * NUMBERS. localStorage is shared by every account that signs in on the same
 * browser, so the cache MUST be scoped to the user who created it — otherwise
 * the next person to log in sees the previous barrister's matters on their
 * dashboard. We stamp every cache with `ownerUserId` and wipe it whenever the
 * logged-in user changes (logout, account switch) or on reload if the stored
 * owner no longer matches the signed-in user.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAuthStore } from '@/features/auth/store';
import { registerUserScopedStore, currentOwnerId } from '@/lib/userScopedPersist';

export interface QuickAccessMatter {
  id: string;
  name: string;
  number: string;
  accessedAt: number;
}

interface MatterQuickAccessState {
  /** The user these cached matters belong to. Null = nobody / logged out. */
  ownerUserId: string | null;
  /** Pinned matters (user explicitly pinned) */
  pinnedMatters: QuickAccessMatter[];
  /** Recently accessed matters (auto-tracked) */
  recentMatters: QuickAccessMatter[];
  /** Maximum number of recent matters to track */
  maxRecentMatters: number;

  // Actions
  pinMatter: (matter: Omit<QuickAccessMatter, 'accessedAt'>) => void;
  unpinMatter: (matterId: string) => void;
  isPinned: (matterId: string) => boolean;
  trackAccess: (matter: Omit<QuickAccessMatter, 'accessedAt'>) => void;
  clearRecentMatters: () => void;
  /** Wipe all cached matters and re-stamp for the given user. */
  resetForUser: (userId: string | null) => void;
}

const currentUserId = currentOwnerId;

export const useMatterQuickAccessStore = create<MatterQuickAccessState>()(
  persist(
    (set, get) => ({
      ownerUserId: null,
      pinnedMatters: [],
      recentMatters: [],
      maxRecentMatters: 5,

      pinMatter: (matter) => {
        const uid = currentUserId();
        set((state) => {
          // If the cache belongs to a different user, start clean under the
          // current user before adding — never merge across accounts.
          const base =
            state.ownerUserId === uid
              ? state
              : { ...state, ownerUserId: uid, pinnedMatters: [], recentMatters: [] };
          if (base.pinnedMatters.some((m) => m.id === matter.id)) return base;
          return {
            ...base,
            pinnedMatters: [...base.pinnedMatters, { ...matter, accessedAt: Date.now() }],
          };
        });
      },

      unpinMatter: (matterId) => {
        set((state) => ({
          pinnedMatters: state.pinnedMatters.filter((m) => m.id !== matterId),
        }));
      },

      isPinned: (matterId) => {
        // Another user's cache is never "pinned" for the current user.
        if (get().ownerUserId !== currentUserId()) return false;
        return get().pinnedMatters.some((m) => m.id === matterId);
      },

      trackAccess: (matter) => {
        const uid = currentUserId();
        set((state) => {
          const base =
            state.ownerUserId === uid
              ? state
              : { ...state, ownerUserId: uid, pinnedMatters: [], recentMatters: [] };

          // Don't add to recent if already pinned
          if (base.pinnedMatters.some((m) => m.id === matter.id)) return base;

          const filtered = base.recentMatters.filter((m) => m.id !== matter.id);
          const updated = [{ ...matter, accessedAt: Date.now() }, ...filtered].slice(
            0,
            base.maxRecentMatters,
          );

          return { ...base, ownerUserId: uid, recentMatters: updated };
        });
      },

      clearRecentMatters: () => {
        set({ recentMatters: [] });
      },

      resetForUser: (userId) => {
        set({ ownerUserId: userId, pinnedMatters: [], recentMatters: [] });
      },
    }),
    {
      name: 'matter-quick-access',
      // v2: introduced per-user scoping. Wipe any pre-scoping cache on upgrade
      // so no matter cached under the old (unscoped) key can leak to a new user.
      version: 2,
      migrate: () => ({
        ownerUserId: null,
        pinnedMatters: [],
        recentMatters: [],
        maxRecentMatters: 5,
      }),
    },
  ),
);

// Cross-user confidentiality guard (see userScopedPersist): wipe cached matters
// whenever the signed-in user changes or on reload if the owner no longer matches.
registerUserScopedStore({
  getOwner: () => useMatterQuickAccessStore.getState().ownerUserId,
  reset: (uid) => useMatterQuickAccessStore.getState().resetForUser(uid),
});

/**
 * Hook to get combined quick access matters (pinned first, then recent).
 * Returns nothing unless the cache belongs to the currently signed-in user.
 */
export function useQuickAccessMatters() {
  const currentUid = useAuthStore((s) => s.user?.id ?? null);
  const ownerUserId = useMatterQuickAccessStore((s) => s.ownerUserId);
  const pinnedMatters = useMatterQuickAccessStore((s) => s.pinnedMatters);
  const recentMatters = useMatterQuickAccessStore((s) => s.recentMatters);

  // Belt-and-braces: even before reconciliation runs, never surface another
  // user's cached matters.
  const owned = ownerUserId === currentUid;
  const pinned = owned ? pinnedMatters : [];
  const recent = owned ? recentMatters : [];

  // Filter out any recent matters that are also in pinned (shouldn't happen but safety check)
  const pinnedIds = new Set(pinned.map((m) => m.id));
  const filteredRecent = recent.filter((m) => !pinnedIds.has(m.id));

  return {
    pinned,
    recent: filteredRecent,
    hasQuickAccess: pinned.length > 0 || filteredRecent.length > 0,
  };
}
